import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/contexts/SubscriptionContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, CreditCard, RefreshCw, Star, AlertTriangle, PartyPopper, Zap, ArrowRight } from "lucide-react";
import { getReferralData } from "@/utils/referralTracking";

// Helper to fire conversion events only once per session
const fireTrialConversion = (sessionId) => {
  const storageKey = `trial_conversion_fired_${sessionId}`;
  
  // Check if already fired for this session
  if (sessionStorage.getItem(storageKey)) {
    console.log('[Conversion] Already fired for this session, skipping');
    return Promise.resolve();
  }
  
  // Mark as fired immediately to prevent duplicates
  sessionStorage.setItem(storageKey, 'true');
  
  return new Promise((resolve) => {
    if (typeof window.gtag === 'function') {
      // Fire GA4 trial_started event
      window.gtag('event', 'trial_started');
      
      // Fire Google Ads conversion event
      window.gtag('event', 'conversion', {
        'send_to': 'AW-659695065/DRPvCNGZgIEcENnLyLoC',
        'event_callback': () => {
          console.log('[Conversion] Google Ads conversion fired successfully');
          resolve();
        }
      });
      
      // Fallback timeout in case callback doesn't fire
      setTimeout(resolve, 500);
    } else {
      console.warn('[Conversion] gtag not available');
      resolve();
    }
  });
};

const PLANS = [
  {
    id: "professional",
    name: "Professional",
    monthlyPrice: 49,
    annualPrice: 39,
    annualTotal: 468,
    description: "For growing businesses",
    popular: true,
    features: [
      "10 forms",
      "500 submissions/month",
      "AI lead scoring & summaries",
      "Full CRM pipeline",
      "Team collaboration",
      "Priority support"
    ]
  },
  {
    id: "starter",
    name: "Starter",
    monthlyPrice: 19,
    annualPrice: 15,
    annualTotal: 192,
    description: "Perfect for small businesses",
    features: [
      "3 forms",
      "100 submissions/month",
      "AI lead scoring & summaries",
      "Email notifications",
      "Basic CRM pipeline"
    ]
  }
];

export default function CompleteSignupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const { 
    status, 
    isLoading: subscriptionLoading, 
    hasAccess,
    startPolling,
    stopPolling,
    refreshSubscription 
  } = useSubscription();
  
  const [selectedPlan, setSelectedPlan] = useState("professional");
  const [billingCycle, setBillingCycle] = useState("annual"); // Default to annual
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(location.state?.payment_failed || false);
  const [showRetry, setShowRetry] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false); // NEW: Show strong success UI
  
  // Detect promo code from URL
  const promoCode = new URLSearchParams(location.search).get("promo");

  // Referral capture is now handled universally in App.js
  // No need to capture here - just read from localStorage when creating checkout

  // Check if user already has active subscription using context
  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    // Wait for subscription context to finish loading
    if (!subscriptionLoading) {
      // If already active or trialing, redirect to dashboard
      if (hasAccess) {
        navigate("/dashboard", { replace: true });
        return;
      }
      setCheckingStatus(false);
    }
  }, [token, subscriptionLoading, hasAccess, navigate]);

  // Confirm checkout session helper
  const confirmCheckoutSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API}/payments/confirm-checkout-session`,
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error("Failed to confirm checkout session:", error);
      return null;
    }
  };

  // Check for success/cancel from Stripe
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    
    if (params.get("success") === "true" && sessionId) {
      // Payment succeeded - confirm the checkout session directly
      setLoading(true);
      
      const confirmAndActivate = async () => {
        // Step 1: Try to confirm the checkout session directly (most reliable)
        const confirmResult = await confirmCheckoutSession(sessionId);
        
        if (confirmResult?.status === "confirmed" && 
            ["trialing", "intro", "active"].includes(confirmResult.subscription_status)) {
          // Success! Show success UI first, then redirect
          await refreshSubscription();
          
          // Fire conversion events (GA4 + Google Ads)
          await fireTrialConversion(sessionId);
          
          // Show success confirmation UI
          setLoading(false);
          setPaymentSuccess(true);
          return;
        }
        
        // Step 2: If confirm didn't work, try sync endpoint
        try {
          await axios.post(`${API}/subscription/sync`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const statusRes = await refreshSubscription();
          if (statusRes && ["trialing", "intro", "active"].includes(statusRes.subscription_status)) {
            // Fire conversion events (GA4 + Google Ads)
            await fireTrialConversion(sessionId);
            
            // Show success confirmation UI
            setLoading(false);
            setPaymentSuccess(true);
            return;
          }
        } catch (e) {
          // Ignore sync errors
        }
        
        // Step 3: Fall back to polling (in case webhook updates the status)
        startPolling(
          // onSuccess
          async (data) => {
            // Fire conversion events before showing success
            await fireTrialConversion(sessionId);
            setLoading(false);
            setPaymentSuccess(true);
          },
          // onTimeout
          () => {
            setLoading(false);
            setShowRetry(true);
            toast.info("Still processing your payment. Please click 'Retry' below.", {
              duration: 10000
            });
          }
        );
      };
      
      confirmAndActivate();
    } else if (params.get("success") === "true" && !sessionId) {
      // Success but no session ID - try sync
      setLoading(true);
      axios.post(`${API}/subscription/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(() => refreshSubscription())
        .then(async (statusRes) => {
          if (statusRes && ["trialing", "intro", "active"].includes(statusRes.subscription_status)) {
            // Fire conversion events (use generic key since no session_id)
            await fireTrialConversion('no_session_id');
            setLoading(false);
            setPaymentSuccess(true);
          } else {
            setLoading(false);
            setShowRetry(true);
          }
        })
        .catch(() => {
          setLoading(false);
          setShowRetry(true);
        });
    } else if (params.get("canceled") === "true") {
      toast.info("Checkout canceled. Select a plan to continue.");
    }
    
    // Cleanup polling on unmount
    return () => stopPolling();
  }, [location.search, startPolling, stopPolling, navigate, token, refreshSubscription]);

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      // Get referral data from localStorage (captured universally)
      const refData = getReferralData();
      
      const response = await axios.post(
        `${API}/payments/create-subscription`,
        {
          plan_id: selectedPlan,
          billing_cadence: billingCycle,
          success_url: `${window.location.origin}/complete-signup?success=true`,
          cancel_url: `${window.location.origin}/complete-signup?canceled=true`,
          // Include referral tracking data (always from localStorage)
          referral_code: refData.referral_code,
          utm_source: refData.utm_source,
          utm_campaign: refData.utm_campaign
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error("No checkout URL returned. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'string') {
        if (errorDetail.includes('price') || errorDetail.includes('Price')) {
          toast.error("Billing configuration error. Please contact support.");
        } else {
          toast.error(errorDetail);
        }
      } else {
        toast.error("Failed to start checkout. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleRefreshStatus = async () => {
    setRefreshingStatus(true);
    try {
      // Get session_id from URL if present
      const params = new URLSearchParams(location.search);
      const sessionId = params.get("session_id");
      
      // Try confirm-checkout-session first if we have a session ID
      if (sessionId) {
        try {
          const confirmRes = await axios.post(
            `${API}/payments/confirm-checkout-session`,
            { session_id: sessionId },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (confirmRes.data?.status === "confirmed" && 
              ["trialing", "intro", "active"].includes(confirmRes.data.subscription_status)) {
            await refreshSubscription();
            
            // Fire conversion events (GA4 + Google Ads)
            await fireTrialConversion(sessionId);
            
            // Show success UI
            setRefreshingStatus(false);
            setPaymentSuccess(true);
            return;
          }
        } catch (e) {
          // Fall through to sync
        }
      }
      
      // Try sync from Stripe directly
      await axios.post(`${API}/subscription/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh subscription context
      const data = await refreshSubscription();
      
      if (data && (data.subscription_status === "active" || 
                   data.subscription_status === "trialing" || 
                   data.subscription_status === "intro")) {
        // Fire conversion events (GA4 + Google Ads)
        await fireTrialConversion(sessionId || 'manual_refresh');
        
        // Show success UI
        setRefreshingStatus(false);
        setPaymentSuccess(true);
      } else {
        toast.info("Your account is not yet active. If you've paid, please wait a moment and try again.");
      }
    } catch (error) {
      console.error("Refresh status error:", error);
      toast.error("Failed to check status. Please try again.");
    } finally {
      setRefreshingStatus(false);
    }
  };

  if (checkingStatus || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Checking account status...</p>
        </div>
      </div>
    );
  }

  // Show processing state when returning from Stripe checkout
  const params = new URLSearchParams(location.search);
  const isProcessingPayment = loading && params.get("success") === "true";
  
  // ============================================
  // STRONG SUCCESS OVERRIDE UI
  // This eliminates any confusion from Stripe UI inconsistencies
  // ============================================
  if (paymentSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-[#f5f5f0]" data-testid="payment-success-screen">
        <div className="text-center max-w-md px-6">
          {/* Success Icon */}
          <div className="relative mb-6">
            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <PartyPopper className="w-12 h-12 text-green-600" />
            </div>
            <div className="absolute -top-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
              <Check className="w-5 h-5 text-white" />
            </div>
          </div>
          
          {/* Main Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-3" data-testid="success-title">
            You're in! 🎉
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            Your 7-day trial has started.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            No charge today. Your card will be charged on day 8 unless you cancel.
          </p>
          
          {/* What's Next */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-6 text-left">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              What's next?
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">1</span>
                </div>
                <span className="text-sm text-gray-600">Create your first intake form</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">2</span>
                </div>
                <span className="text-sm text-gray-600">Share with your clients</span>
              </li>
              <li className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary">3</span>
                </div>
                <span className="text-sm text-gray-600">Watch leads flow in automatically</span>
              </li>
            </ul>
          </div>
          
          {/* CTA Button */}
          <Button
            size="lg"
            className="rounded-full px-8 py-6 text-lg w-full bg-primary hover:bg-primary/90"
            onClick={() => navigate("/dashboard", { replace: true })}
            data-testid="continue-to-dashboard-btn"
          >
            Continue to Dashboard
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          {/* Reassurance */}
          <p className="text-xs text-gray-400 mt-4">
            Payment confirmed • Trial active • Full access unlocked
          </p>
        </div>
      </div>
    );
  }
  
  if (isProcessingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
          <h2 className="text-xl font-bold text-foreground mb-2">Finalizing your account...</h2>
          <p className="text-muted-foreground mb-4">
            We're setting up your subscription. This usually takes just a few seconds.
          </p>
          <div className="w-48 h-2 bg-gray-200 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
          {showRetry && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
              <p className="text-sm text-yellow-800 mb-3">
                Taking longer than expected? Your payment was received.
              </p>
              <Button 
                onClick={handleRefreshStatus} 
                disabled={refreshingStatus}
                className="rounded-full"
              >
                {refreshingStatus ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Retry Activation
              </Button>
              <p className="text-xs text-yellow-600 mt-2">
                Still stuck? <a href="mailto:hello@heyintake.com" className="underline">Contact support</a>
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f0] flex flex-col" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-white rounded-full" />
            </div>
            <span className="font-bold text-xl tracking-tight">HeyIntake</span>
          </div>
          <Button variant="ghost" onClick={handleLogout} className="text-muted-foreground">
            Sign Out
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-3xl">
          {/* Payment Failed Banner */}
          {paymentFailed && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-3" data-testid="payment-failed-banner">
              <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Payment Failed</h3>
                <p className="text-sm text-muted-foreground">
                  Your payment method was declined. Update your payment method to continue using HeyIntake.
                </p>
              </div>
            </div>
          )}

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-3" data-testid="complete-signup-headline">
              {paymentFailed ? "Update payment method to continue" : "Complete your signup"}
            </h1>
            <p className="text-muted-foreground text-lg">
              {paymentFailed 
                ? "Your subscription was paused due to a payment issue. Choose a plan to restore access."
                : "Start your 7-day free trial. Renews at your selected plan price on day 8 unless canceled."}
            </p>
          </div>

          {/* Billing Cycle Toggle */}
          <div className="flex items-center justify-center gap-3 mb-8" data-testid="billing-toggle">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                billingCycle === "monthly"
                  ? "bg-primary text-white"
                  : "bg-white text-muted-foreground hover:bg-gray-100 border border-border"
              }`}
              data-testid="billing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === "annual"
                  ? "bg-primary text-white"
                  : "bg-white text-muted-foreground hover:bg-gray-100 border border-border"
              }`}
              data-testid="billing-toggle-annual"
            >
              Annual
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                billingCycle === "annual" ? "bg-white/20" : "bg-green-100 text-green-700"
              }`}>
                Save 20%
              </span>
            </button>
          </div>

          {/* Plan Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {PLANS.map((plan) => (
              <Card
                key={plan.id}
                className={`relative cursor-pointer transition-all duration-200 ${
                  selectedPlan === plan.id
                    ? "border-2 border-primary shadow-lg ring-2 ring-primary/20"
                    : "border-border hover:border-primary/50"
                } ${plan.popular ? "md:scale-105" : ""}`}
                onClick={() => setSelectedPlan(plan.id)}
                data-testid={`plan-card-${plan.id}`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white">
                    <Star className="w-3 h-3 mr-1 fill-white" />
                    Most Popular
                  </Badge>
                )}
                <CardContent className="p-6 pt-8">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold">{plan.name}</h3>
                      <p className="text-sm text-muted-foreground">{plan.description}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === plan.id
                        ? "border-primary bg-primary"
                        : "border-muted-foreground/30"
                    }`}>
                      {selectedPlan === plan.id && (
                        <Check className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </div>

                  <div className="mb-6">
                    <span className="text-4xl font-bold">
                      ${billingCycle === "annual" ? plan.annualPrice : plan.monthlyPrice}
                    </span>
                    <span className="text-muted-foreground">/month</span>
                    {billingCycle === "annual" && (
                      <span className="ml-2 text-sm text-muted-foreground line-through">
                        ${plan.monthlyPrice}
                      </span>
                    )}
                    {billingCycle === "annual" && (
                      <div className="text-sm text-muted-foreground mt-1">
                        ${plan.annualTotal}/year (billed annually)
                      </div>
                    )}
                  </div>

                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center">
            {/* Pre-checkout info about Link */}
            <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-xl text-left" data-testid="link-info-box">
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Fast checkout with Link</p>
                  <p className="text-xs text-blue-700 mt-1">
                    You can pay instantly with <strong>Link</strong> (recommended) or enter your card details manually.
                  </p>
                </div>
              </div>
            </div>
            
            <Button
              size="lg"
              className="rounded-full px-8 py-6 text-lg"
              onClick={handleStartTrial}
              disabled={loading}
              data-testid="start-trial-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CreditCard className="w-5 h-5 mr-2" />
              )}
              Start 7-Day Free Trial
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Renews at ${billingCycle === "annual" 
                ? PLANS.find(p => p.id === selectedPlan)?.annualPrice 
                : PLANS.find(p => p.id === selectedPlan)?.monthlyPrice}/{billingCycle === "annual" ? "month (billed annually)" : "month"} on day 8 unless canceled.
            </p>
            {promoCode ? (
              <p className="text-sm text-primary mt-2 flex items-center justify-center gap-1">
                <Check className="w-4 h-4" />
                Promo code <span className="font-semibold">{promoCode}</span> will be applied at checkout
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">
                Have a code? Enter it at checkout
              </p>
            )}
          </div>

          {/* Already Paid Link */}
          <div className="text-center mt-6">
            <button
              onClick={handleRefreshStatus}
              disabled={refreshingStatus}
              className="text-sm text-primary hover:underline inline-flex items-center gap-2"
              data-testid="refresh-status-link"
            >
              {refreshingStatus ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Already paid? Refresh status
            </button>
          </div>

          {/* Help Link */}
          <div className="text-center mt-6">
            <p className="text-sm text-muted-foreground">
              Have questions?{" "}
              <a href="mailto:hello@heyintake.com" className="text-primary hover:underline">
                Contact us
              </a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
